import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Badge, Box, Button, Chip, IconButton, Stack, SwipeableDrawer, Typography } from "@mui/material";
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";

import { useAppStatus } from "../status/useAppStatus";
import { appIssuePalette } from "../status/appIssuePalette";
import { APP_UI } from "../ui/appUi";
import { formatAppIssueDateTime } from "../utils/formatAppDateTime";
import { useSheetDismiss } from "../hooks/useSheetDismiss";
import AppCard from "./AppCard";
import FastScrollThumb from "./FastScrollThumb";
import SheetScrollbarGuard from "./SheetScrollbarGuard";

export default function AppNotificationsButton() {
  const { issues, clearIssue, clearAllIssues } = useAppStatus();
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { requestClose } = useSheetDismiss(open, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  const dismissIssue = (event: ReactMouseEvent, id: string) => {
    event.stopPropagation();
    // Eine einzelne Meldung quittieren darf das Top-Sheet nicht schließen.
    // So können mehrere Meldungen nacheinander abgearbeitet werden, ohne das
    // Benachrichtigungs-Icon jedes Mal erneut öffnen zu müssen.
    clearIssue(id);
  };
  const dismissAll = (event: ReactMouseEvent) => {
    event.stopPropagation();
    setOpen(false);
    clearAllIssues();
  };
  const runIssueAction = (event: ReactMouseEvent, actionEvent: string) => {
    event.stopPropagation();
    setOpen(false);
    window.dispatchEvent(new Event(actionEvent));
  };

  const notificationCount = issues.length;
  // Während das Sheet offen ist, bleibt es auch nach dem Quittieren der letzten
  // einzelnen Meldung gemountet und zeigt einen klaren Leerzustand.
  if (notificationCount === 0 && !open) return null;

  const newestFirst = [...issues].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <>
      <Badge badgeContent={notificationCount} color="error" overlap="circular">
        <IconButton
          aria-label={`${notificationCount} offene Meldungen`}
          title="Meldungen"
          onClick={() => setOpen(true)}
          sx={{ width: 42, height: 42, color: APP_UI.warning, border: "1px solid rgba(244,163,64,.35)", bgcolor: APP_UI.warningSurface }}
        >
          <NotificationsActiveRoundedIcon fontSize="small" />
        </IconButton>
      </Badge>

      <SwipeableDrawer
        anchor="top"
        open={open}
        onOpen={() => undefined}
        onClose={requestClose}
        disableSwipeToOpen
        disableDiscovery
        disableBackdropTransition
        hysteresis={0.20}
        minFlingVelocity={220}
        transitionDuration={{ enter: 200, exit: 180 }}
        ModalProps={{ disableRestoreFocus: true, keepMounted: true }}
        slotProps={{
          paper: {
            sx: {
              mt: "env(safe-area-inset-top)",
              mx: "auto",
              width: "100%",
              maxWidth: 600,
              maxHeight: "72dvh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              borderRadius: "0 0 26px 26px",
              color: APP_UI.text,
              backgroundColor: "rgba(22,23,26,.985)",
              backgroundImage: "none",
              border: `1px solid ${APP_UI.border}`,
            },
          },
        }}
        sx={{ zIndex: 1750, "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,.68)" } }}
      >
        <Box ref={contentRef} sx={{ p: 0, overflowY:"auto", overscrollBehavior:"contain", minHeight: 0, flex: "1 1 auto" }}>
          <Stack spacing={0.85} sx={{ px:1.5, pb:1.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems:"center" }}>
              <Box sx={{ flex:1 }}><Typography variant="h6" sx={{ fontWeight:900 }}>Meldungen</Typography><Typography variant="caption" sx={{ color:APP_UI.textMuted }}>{notificationCount} offene Meldung{notificationCount===1?"":"en"}</Typography></Box>
              <IconButton aria-label="Meldungen schließen" title="Schließen" onClick={requestClose} sx={{ color:"#fff" }}><CloseRoundedIcon /></IconButton>
            </Stack>
            {notificationCount === 0 && (
              <AppCard sx={{ borderColor: APP_UI.accentBorder, bgcolor: APP_UI.accentSurface }}>
                <Typography sx={{ fontWeight: 900 }}>Keine offenen Meldungen</Typography>
                <Typography variant="caption" sx={{ color: APP_UI.textMuted, display: "block", mt: .2 }}>Alle einzelnen Meldungen wurden quittiert. Du kannst das Top-Sheet jetzt schließen.</Typography>
              </AppCard>
            )}
            {newestFirst.map((issue) => {
              const palette = appIssuePalette(issue);
              return (
                <AppCard key={issue.id} sx={{ p:1, borderColor:palette.border, bgcolor:palette.background }}>
                  <Stack direction="row" spacing={0.8} sx={{ alignItems:"flex-start" }}>
                    <Box sx={{ width:9,height:9,mt:.65,flex:"0 0 auto",borderRadius:"50%",bgcolor:palette.accent }} />
                    <Box sx={{ minWidth:0,flex:1 }}>
                      <Stack direction="row" spacing={0.65} sx={{ alignItems:"center", flexWrap:"wrap", rowGap:0.45 }}>
                        {issue.adminOnly && <Chip label="ADMIN" size="small" sx={{ height:20, color:palette.chipText, border:`1px solid ${palette.chipBorder}`, bgcolor:palette.chipBackground, fontWeight:950, fontSize:"0.64rem", letterSpacing:".055em" }} />}
                        {issue.diagnosticTest && <Chip label="Diagnose-Test" size="small" sx={{ height:20, color:"#b78cff", border:"1px solid rgba(183,140,255,.38)", bgcolor:"rgba(183,140,255,.09)", fontWeight:950, fontSize:"0.64rem" }} />}
                        <Typography sx={{ color:APP_UI.text,fontWeight:850 }}>{issue.title}</Typography>
                      </Stack>
                      {issue.detail&&<Typography variant="caption" sx={{ color:APP_UI.textMuted,display:"block",overflowWrap:"anywhere", mt:.2 }}>{issue.detail}</Typography>}
                      <Typography variant="caption" sx={{ color:"#777b82",display:"block",mt:.18 }}>{formatAppIssueDateTime(issue.createdAt)}</Typography>
                      {issue.actionLabel && issue.actionEvent && <Button size="small" variant="outlined" onClick={(event)=>runIssueAction(event, issue.actionEvent!)} sx={{ mt:.6, minHeight:32, color:palette.accent,borderColor:palette.border,textTransform:"none",fontWeight:850 }}>{issue.actionLabel}</Button>}
                    </Box>
                    <Button size="small" onClick={(event)=>dismissIssue(event, issue.id)} sx={{ minWidth:0,color:palette.accent,textTransform:"none",fontWeight:850 }}>OK</Button>
                  </Stack>
                </AppCard>
              );
            })}
            {issues.length > 0 && <Button variant="outlined" startIcon={<DoneAllRoundedIcon />} onClick={dismissAll} sx={{ borderRadius:999,color:APP_UI.accent,borderColor:APP_UI.accentBorder,textTransform:"none",fontWeight:850 }}>Alle Meldungen quittieren</Button>}
          </Stack>
        </Box>
        <SheetScrollbarGuard>
          <FastScrollThumb targetRef={contentRef} contained minScrollablePx={1} zIndex={1800} right={1} />
        </SheetScrollbarGuard>
        <Box sx={{ minHeight: 42, display:"grid", placeItems:"center", touchAction:"pan-y", cursor: "grab" }}>
          <Box sx={{ width:44, height:4, borderRadius:99, bgcolor:"rgba(255,255,255,.30)" }} />
        </Box>
      </SwipeableDrawer>
    </>
  );
}
